'use client';

/**
 * IntentLinesCard — per-line breakdown rail card for mode3.
 *
 * Selection-aware: renders only when an Intent is the active
 * viewport selection. Reads from `intent_details` (not `intent_lines`)
 * because that strategy returns the runtime-shape `IntentSummary.lines`
 * carrying `_format_line_brief` output — `brief` is the structured
 * descriptor (`storage unit · buffet · wood · butter · contemporary,
 * minimalist · elegant, serene`) and `intent_description` is the
 * italic per-line prose. The basic `intent_lines` strategy returns
 * IntentLineRow which lacks both.
 *
 * Sharing the `intent_details` fetch with `IntentBriefCard` and
 * `IntentMatchQualityCard` means React Query dedups all three rail
 * cards onto a single BFF call — same React Query key, one network
 * round trip per Intent selection.
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_lines"
 * @see klyde_edge/discovery/intent_ingest/review.py::_format_line_brief
 *
 * @component_role: Card
 * @component_type: Rail Card
 * @domain: Copilot / Intent
 * @layer: presentation
 */

import { useMemo } from 'react';
import { Loader2, List } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardExport } from '@/lib/design/card-export';
import type { IntentListResponse } from '@/lib/api/types';
import { RailCard } from '../rail-card';

const INTENT_DETAILS_STRATEGY = 'intent_details';

// Runtime line shape (server emits via `_format_line_brief`). Not on
// the IntentSummary TS type; declared locally so the card reads with
// type safety without adding fields to the public API surface.
interface IntentDetailLine {
  line_sid?: string | null;
  name: string;
  qty?: number | null;
  target_price?: number | null;
  brief?: string | null;
  intent_description?: string | null;
}

export function IntentLinesCard() {
  const { entityType, entityId } = useViewportSelection();
  const enabled = entityType === 'intent' && !!entityId;

  const { data, isLoading, error } = useStrategyData<IntentListResponse>(
    INTENT_DETAILS_STRATEGY,
    enabled ? { intent_sid: entityId ?? '' } : {},
    enabled,
  );

  // Scene export — see IntentBriefCard for the doctrine. Shares the
  // `intent_details` query_key with the other two rail cards (one cache
  // entry, one payload in the envelope).
  useCardExport(
    useMemo(() => {
      const it = data?.data?.items?.[0] as unknown as
        | { lines?: IntentDetailLine[] }
        | undefined;
      const lines = it?.lines ?? [];
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading
          ? 'loading'
          : error
            ? 'error'
            : it
              ? 'loaded'
              : 'idle';
      const fmtLine = (l: IntentDetailLine) =>
        `${l.name || '(unnamed)'} × ${l.qty ?? 1}` +
        `${l.brief ? ` — ${l.brief}` : ''}` +
        `${l.intent_description ? ` — “${l.intent_description}”` : ''}`;
      return {
        label: lines.length ? `Lines (${lines.length})` : 'Lines',
        content_key: 'intent_lines',
        surface: 'rail' as const,
        status,
        query_key: enabled
          ? ['strategy-data', INTENT_DETAILS_STRATEGY, { intent_sid: entityId ?? '' }]
          : null,
        text: lines.length ? lines.map(fmtLine).join('\n') : 'Lines',
        markdown: lines.length
          ? [`## Lines (${lines.length})`, ...lines.map((l) => `- ${fmtLine(l)}`)].join('\n')
          : '## Lines',
        json: {
          count: lines.length,
          lines: lines.map((l) => ({
            line_sid: l.line_sid ?? null,   // identity (correlates scene↔payload)
            name: l.name || '(unnamed)',
            qty: `× ${l.qty ?? 1}`,         // painted, as the card renders it
            brief: l.brief ?? null,
            description: l.intent_description ?? null,
          })),
        },
      };
    }, [data, enabled, entityId, isLoading, error]),
  );

  if (!enabled) {
    return (
      <RailCard title="Lines" icon={List}>
        <div className="text-xs text-muted-foreground">Select an Intent to see its lines</div>
      </RailCard>
    );
  }

  if (isLoading) {
    return (
      <RailCard title="Lines" icon={List}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading lines…
        </div>
      </RailCard>
    );
  }

  if (error || !data?.data) {
    return (
      <RailCard title="Lines" icon={List}>
        <div className="text-xs text-destructive">
          {error ? `Failed to load: ${(error as Error).message}` : 'No data'}
        </div>
      </RailCard>
    );
  }

  // `lines` is on the runtime IntentSummary (via `_format_line_brief`)
  // but absent from the typed shape — cast through unknown to read it
  // without coupling to a type-incomplete edge.
  const intent = data.data.items?.[0] as unknown as
    | { lines?: IntentDetailLine[] }
    | undefined;
  const lines = intent?.lines ?? [];

  if (lines.length === 0) {
    return (
      <RailCard title="Lines" icon={List}>
        <div className="text-xs text-muted-foreground">No lines on this Intent.</div>
      </RailCard>
    );
  }

  return (
    <RailCard title={`Lines (${lines.length})`} icon={List}>
      <ul className="space-y-2">
        {lines.map((l, idx) => (
          <li
            key={l.line_sid ?? `${l.name}-${idx}`}
            className="p-2 rounded-lg bg-card border border-border space-y-1"
          >
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-xs font-medium truncate flex-1">
                {l.name || '(unnamed)'}
              </p>
              <span className="text-[10px] text-muted-foreground flex-shrink-0">
                × {l.qty ?? 1}
              </span>
            </div>
            {l.brief && (
              <p className="text-[10px] text-muted-foreground leading-snug">
                {l.brief}
              </p>
            )}
            {l.intent_description && (
              <p className="text-[11px] italic text-muted-foreground/80 leading-relaxed">
                “{l.intent_description}”
              </p>
            )}
          </li>
        ))}
      </ul>
    </RailCard>
  );
}
